import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { carbonGovernanceService } from '../../api/services';
import { ArticleACompleter, CarbonCategoryFactor, CarbonGovernanceSummary } from '../../types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Gouvernance carbone (TF-035 → TF-038) : fiabilité des données carbone. Affiche la couverture
 * des facteurs d'émission, les articles à compléter, le CO₂ figé total / estimé, et permet de
 * configurer les facteurs monétaires de repli par catégorie.
 */
export function GouvernanceCarbone() {
  const [summary, setSummary] = useState<CarbonGovernanceSummary | null>(null);
  const [articles, setArticles] = useState<ArticleACompleter[]>([]);
  const [factors, setFactors] = useState<CarbonCategoryFactor[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      carbonGovernanceService.getSummary().then(setSummary),
      carbonGovernanceService.getArticlesACompleter().then(setArticles),
      carbonGovernanceService.getCategoryFactors().then(setFactors),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <LoadingSpinner />;

  const couverture = summary?.couvertureFacteur ?? 0;
  const couvColor = couverture >= 80 ? 'text-green-600' : couverture >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Gouvernance carbone</h1>
        <p className="page-subtitle">Fiabilité des données carbone : un bilan sourcé, daté et auditable</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card">
          <p className="text-xs text-gray-500">Couverture facteurs</p>
          <p className={`text-2xl font-bold ${couvColor}`}>{couverture}%</p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Articles à compléter</p>
          <p className="text-2xl font-bold text-amber-600">{summary?.articlesSansFacteur ?? 0}</p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">CO₂ figé total</p>
          <p className="text-2xl font-bold text-emerald-600">
            {(summary?.totalCo2Fige ?? 0).toLocaleString('fr-FR')} <span className="text-sm">kg</span>
          </p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Documents estimés</p>
          <p className="text-2xl font-bold text-gray-700">{summary?.documentsEstimes ?? 0}</p>
        </div>
      </div>

      {/* Articles à compléter */}
      <div className="card">
        <h2 className="font-semibold mb-3">Articles sans facteur d’émission ({articles.length})</h2>
        {articles.length === 0 ? (
          <p className="text-gray-500 text-sm">Tous vos articles actifs ont un facteur d’émission. 🎉</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-2">Référence</th>
                  <th className="px-3 py-2">Désignation</th>
                  <th className="px-3 py-2">Catégorie</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {articles.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{a.reference}</td>
                    <td className="px-3 py-2 text-gray-600">{a.designation}</td>
                    <td className="px-3 py-2 text-gray-500">{a.categorie ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/articles/${a.id}/edit`} className="text-blue-600 hover:underline">Compléter →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CategoryFactorEditor factors={factors} onChange={load} />
    </div>
  );
}

function CategoryFactorEditor({ factors, onChange }:
  { factors: CarbonCategoryFactor[]; onChange: () => void }) {
  const [categorie, setCategorie] = useState('');
  const [facteur, setFacteur] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!categorie.trim() || facteur === '') return;
    setSaving(true);
    try {
      await carbonGovernanceService.upsertFactor(categorie.trim(), Number(facteur));
      setCategorie(''); setFacteur('');
      onChange();
    } finally { setSaving(false); }
  };

  const remove = async (id: number) => { await carbonGovernanceService.deleteFactor(id); onChange(); };

  return (
    <div className="card">
      <h2 className="font-semibold mb-1">Facteurs de repli par catégorie</h2>
      <p className="text-xs text-gray-500 mb-3">
        kg CO₂e par TND dépensé — utilisé pour estimer le CO₂ quand l’article n’a pas de facteur réel.
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Catégorie</label>
          <input className="input-field" value={categorie} onChange={(e) => setCategorie(e.target.value)}
            placeholder="ex. Transport" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Facteur (kg/TND)</label>
          <input className="input-field w-32" type="number" step="0.0001" value={facteur}
            onChange={(e) => setFacteur(e.target.value)} placeholder="ex. 0.42" />
        </div>
        <button onClick={save} disabled={saving} className="btn-primary">Enregistrer</button>
      </div>

      {factors.length === 0 ? (
        <p className="text-gray-500 text-sm">Aucun facteur de repli défini.</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {factors.map((f) => (
            <li key={f.id} className="flex items-center justify-between py-2">
              <span className="text-gray-700"><span className="font-medium">{f.categorie}</span> — {f.facteurMonetaire} kg/TND</span>
              <button onClick={() => remove(f.id)} className="text-red-600 hover:underline">Supprimer</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default GouvernanceCarbone;
